import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { env } from "env";
import { useAPIMutation } from "lib/query/api-queries";
import { trpc } from "lib/trpc";

export function useBillingStatus() {
    return useSuspenseQuery(trpc.billing.status.queryOptions());
}

/**
 * Whether this account would get free starting credits in a new organization, and which organizations
 * spent that entitlement if not. Keyed on the person, not the active organization.
 *
 * `useQuery` rather than `useSuspenseQuery`: this only decorates a screen with an explanation, so it
 * must never hold one up or fail one.
 */
export function useFreeStartEligibility() {
    return useQuery(trpc.billing.freeStartEligibility.queryOptions());
}

/**
 * The organization's credit balance, or undefined until it resolves.
 *
 * Plain `useQuery`, not the suspense one above: callers use this to decide whether to offer an action,
 * and suspending an onboarding screen on that decision would blank a working page.
 */
/**
 * Whether the org has run its wallet down far enough that the server will refuse new work. A
 * billing-exempt org never has, however low its (frozen) balance reads. Undefined while the status
 * loads - callers must not gate on that, a slow read is not an answer.
 */
export function useIsOutOfCredits(): boolean | undefined {
    const { data } = useQuery(trpc.billing.status.queryOptions());
    if (data == null) return undefined;
    if (!env.VITE_STRIPE_ENABLED) return false;
    return !data.unlimitedCredits && data.creditBalance <= 0;
}

export type BillingStatusData = ReturnType<typeof useBillingStatus>["data"];
export type BillingTransaction = BillingStatusData["transactions"][number];

export function useCreateCheckoutSession() {
    return useAPIMutation({
        ...trpc.billing.createCheckoutSession.mutationOptions(),
    });
}

export function useCreatePortalSession() {
    return useAPIMutation({
        ...trpc.billing.createPortalSession.mutationOptions(),
    });
}

export function useUpdateAutoTopUp() {
    const queryClient = useQueryClient();
    return useAPIMutation({
        ...trpc.billing.updateAutoTopUp.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: trpc.billing.status.queryKey() });
            },
        }),
        successToast: { title: "Auto top-up settings updated" },
    });
}

/**
 * Buys a credit package on the Vercel rail. Unlike the Stripe path there is no redirect - the
 * credits are granted server-side and billed on the next Vercel invoice - so the balance is
 * refetched in place and the toast is the whole confirmation.
 *
 * The spend cap is refetched alongside the balance: the purchase books its price against the org's
 * monthly ceiling, which is the number {@link useSpendCapStatus} renders, and nothing else on the
 * screen would refetch it.
 */
export function usePurchaseVercelCredits() {
    const queryClient = useQueryClient();
    return useAPIMutation({
        ...trpc.billing.purchaseVercelCredits.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: trpc.billing.status.queryKey() });
                void queryClient.invalidateQueries({ queryKey: trpc.billing.getSpendCapStatus.queryKey() });
            },
        }),
    });
}

export function useRedeemPromoCode() {
    const queryClient = useQueryClient();
    return useAPIMutation({
        ...trpc.billing.redeemPromoCode.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: trpc.billing.status.queryKey() });
            },
        }),
        successToast: { title: "Promo code redeemed" },
    });
}

export function useVercelOverageStatus() {
    return useSuspenseQuery(trpc.billing.getVercelOverageStatus.queryOptions());
}

export function useUpdateVercelOverageCap() {
    const queryClient = useQueryClient();
    return useAPIMutation({
        ...trpc.billing.updateVercelOverageCap.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: trpc.billing.getVercelOverageStatus.queryKey() });
            },
        }),
        successToast: { title: "Usage cap updated" },
    });
}

export function useTopupPackages() {
    return useSuspenseQuery(trpc.billing.listTopupPackages.queryOptions());
}

export function useSpendCapStatus() {
    return useSuspenseQuery(trpc.billing.getSpendCapStatus.queryOptions());
}

export function useUpdateSpendCap() {
    const queryClient = useQueryClient();
    return useAPIMutation({
        ...trpc.billing.updateSpendCap.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: trpc.billing.getSpendCapStatus.queryKey() });
            },
        }),
        successToast: { title: "Spend cap updated" },
    });
}
